import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead } from "../utils/sqlClient";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";
import CompanyFactsPanel from "../components/CompanyFactsPanel";

// Friendly names + one-liners for the known prompt categories. Any category not
// listed here still shows up, keyed by its raw name.
const CATEGORY_META = {
  contract_review: {
    label: "Contract Review",
    hint: "Section 2 contract analysis — persona, insurance/commercial/legal standards, and output format.",
  },
  contract_review_chunk_format: {
    label: "Contract Review — Chunk Mode",
    hint: "Per-chunk output format used only during large-PDF chunked analysis. The final synthesis uses the full Contract Review prompt.",
  },
  schedule_review: {
    label: "Schedule Review",
    hint: "Section 9 GC-schedule extractor — what counts as CleanSpace scope and the JSON output shape.",
  },
  scope_summary: {
    label: "Scope Summary",
    hint: "Scope-clarification summarization (inclusions / exclusions / assumptions).",
  },
  commitments_summary: {
    label: "Commitments Summary",
    hint: "Client-commitment summarization at turnover.",
  },
  correspondence: {
    label: "Correspondence Review",
    hint: "Section 14 merged scope-clarifications + client-commitments correspondence analysis.",
  },
};

function categoryLabel(cat) {
  return CATEGORY_META[cat]?.label || cat;
}

function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function SystemPromptsAdmin() {
  const { accounts } = useMsal();
  const user = accounts[0];
  const userEmail = user?.username?.toLowerCase() || "";
  const isAdmin = useIsAdmin();

  const [prompts, setPrompts] = useState([]);
  const [facts, setFacts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(null); // null = closed; object = editing/creating
  const [savedFlash, setSavedFlash] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    sqlRead("EXEC sp_GetAllSystemPrompts")
      .then(setPrompts)
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
    sqlRead("EXEC sp_GetPromptFacts")
      .then(setFacts)
      .catch(e => setError(e.message || String(e)));
  };
  useEffect(load, []);

  // Group prompts by category, each sorted by DisplayOrder.
  const grouped = useMemo(() => {
    const g = {};
    prompts.forEach(p => {
      (g[p.Category] ||= []).push(p);
    });
    Object.values(g).forEach(rows =>
      rows.sort((a, b) => (a.DisplayOrder - b.DisplayOrder) || a.PromptKey.localeCompare(b.PromptKey))
    );
    // Known categories first (in CATEGORY_META order), then any extras alphabetically.
    const known = Object.keys(CATEGORY_META).filter(c => g[c]);
    const extra = Object.keys(g).filter(c => !CATEGORY_META[c]).sort();
    return [...known, ...extra].map(cat => [cat, g[cat]]);
  }, [prompts]);

  const openEdit = (row) => {
    setForm({
      isNew: false,
      PromptKey: row.PromptKey,
      Category: row.Category,
      DisplayOrder: row.DisplayOrder ?? 100,
      PromptText: row.PromptText || "",
      IsActive: !!row.IsActive,
      Notes: row.Notes || "",
      Version: row.Version,
    });
  };

  const openNew = (category) => {
    const siblings = prompts.filter(p => p.Category === category);
    const nextOrder = siblings.length
      ? Math.max(...siblings.map(p => p.DisplayOrder || 0)) + 10
      : 10;
    setForm({
      isNew: true,
      PromptKey: "",
      Category: category,
      DisplayOrder: nextOrder,
      PromptText: "",
      IsActive: true,
      Notes: "",
    });
  };

  const keyExists = form?.isNew &&
    prompts.some(p => p.PromptKey.toLowerCase() === form.PromptKey.trim().toLowerCase());

  const canSave =
    form &&
    form.PromptText.trim() &&
    form.PromptKey.trim() &&
    form.Category.trim() &&
    !keyExists &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        prompt_key: form.PromptKey.trim(),
        category: form.Category.trim(),
        display_order: Number(form.DisplayOrder) || 100,
        prompt_text: form.PromptText,
        is_active: form.IsActive ? 1 : 0,
        updated_by: userEmail || "admin",
        notes: form.Notes?.trim() || null,
      };
      const json = JSON.stringify(payload).replace(/'/g, "''");
      await sqlRead(`EXEC sp_UpsertSystemPrompt @payload = N'${json}'`);
      setSavedFlash(form.PromptKey.trim());
      setTimeout(() => setSavedFlash(""), 2500);
      setForm(null);
      load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    setError("");
    try {
      const payload = {
        prompt_key: row.PromptKey,
        category: row.Category,
        display_order: row.DisplayOrder ?? 100,
        prompt_text: row.PromptText,
        is_active: row.IsActive ? 0 : 1,
        updated_by: userEmail || "admin",
        notes: row.Notes || null,
      };
      const json = JSON.stringify(payload).replace(/'/g, "''");
      await sqlRead(`EXEC sp_UpsertSystemPrompt @payload = N'${json}'`);
      load();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 980, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div className="panel-title">AI System Prompts</div>
        <div className="panel-sub">
          The instructions that drive every AI analysis — contract review, schedule extraction, correspondence,
          and the turnover summaries. Prompts within a category are concatenated in display order, so keep each
          section focused. Changes take effect on the next analysis run; no deploy needed, and a deploy never
          overwrites them. Put company names and insurance figures in Company Facts and reference them as{" "}
          <code>{"{{fact.key}}"}</code> instead of typing them into prompt text.
        </div>
      </div>

      <CompanyFactsPanel
        facts={facts}
        prompts={prompts}
        userEmail={userEmail}
        onChanged={load}
        onError={setError}
      />

      {error && (
        <div style={{
          border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c",
          borderRadius: "var(--radius)", padding: "9px 12px", fontSize: 12, marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      {/* Editor */}
      {form && (
        <div style={{
          border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
          padding: 16, marginBottom: 18, background: "#FAFBFC",
          position: "sticky", top: 96, zIndex: 5,
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {form.isNew ? "New prompt section" : "Edit prompt section"}
            <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text3)", marginLeft: 8 }}>
              {categoryLabel(form.Category)}
              {!form.isNew && form.Version ? ` · v${form.Version}` : ""}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 100px auto", gap: 10, marginBottom: 10, alignItems: "end" }}>
            <Field label="Prompt key *">
              {form.isNew ? (
                <input
                  value={form.PromptKey}
                  onChange={e => setForm(f => ({ ...f, PromptKey: e.target.value }))}
                  placeholder="e.g. contract_review_termination"
                  style={inputStyle}
                />
              ) : (
                <div style={{ ...inputStyle, display: "flex", alignItems: "center", color: "var(--text3)", background: "#f3f4f6" }}>
                  {form.PromptKey}
                </div>
              )}
            </Field>
            <Field label="Order">
              <input
                type="number"
                value={form.DisplayOrder}
                onChange={e => setForm(f => ({ ...f, DisplayOrder: e.target.value }))}
                style={inputStyle}
              />
            </Field>
            <Field label="Active">
              <label style={{ display: "flex", alignItems: "center", gap: 6, height: 30, fontSize: 12, color: "var(--text2)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.IsActive}
                  onChange={e => setForm(f => ({ ...f, IsActive: e.target.checked }))}
                />
                Included in prompt
              </label>
            </Field>
          </div>

          {keyExists && (
            <div style={{ fontSize: 11, color: "#b91c1c", marginBottom: 8 }}>
              A prompt with this key already exists — pick a unique key (or cancel and edit the existing one).
            </div>
          )}

          <Field label="Notes (internal — not sent to the AI)">
            <input
              value={form.Notes}
              onChange={e => setForm(f => ({ ...f, Notes: e.target.value }))}
              style={inputStyle}
            />
          </Field>

          <div style={{ marginTop: 10 }}>
            <Field label="Prompt text *">
              <textarea
                value={form.PromptText}
                onChange={e => setForm(f => ({ ...f, PromptText: e.target.value }))}
                rows={16}
                style={{
                  ...inputStyle, height: "auto", padding: "9px 11px", resize: "vertical",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, lineHeight: 1.5,
                }}
              />
            </Field>
            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 3 }}>
              {form.PromptText.length.toLocaleString()} characters
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                height: 30, padding: "0 16px", fontSize: 12, fontWeight: 600, color: "#fff",
                background: canSave ? "var(--cs-navy)" : "#cbd5e1", border: "none",
                borderRadius: "var(--radius)", cursor: canSave ? "pointer" : "default",
              }}
            >
              {saving ? "Saving…" : "Save prompt"}
            </button>
            <button
              onClick={() => setForm(null)}
              style={{ height: 30, padding: "0 12px", fontSize: 12, color: "var(--text2)", background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading prompts…</div>
      ) : grouped.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          No system prompts found.
        </div>
      ) : (
        grouped.map(([category, rows]) => (
          <div key={category} style={{ marginBottom: 26 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                {categoryLabel(category)}
              </div>
              <code style={{ fontSize: 10, color: "var(--text3)", background: "#f3f4f6", padding: "1px 6px", borderRadius: 4 }}>
                {category}
              </code>
              <div style={{ flex: 1, height: 1, background: "var(--cs-border)" }} />
              <button
                onClick={() => openNew(category)}
                style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", flexShrink: 0, fontWeight: 600 }}
              >
                + Add section
              </button>
            </div>
            {CATEGORY_META[category]?.hint && (
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8 }}>
                {CATEGORY_META[category].hint}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {rows.map(row => {
                const preview = (row.PromptText || "").replace(/\s+/g, " ").trim();
                return (
                  <div key={row.PromptID} style={{
                    border: "1px solid var(--cs-border)", borderRadius: "var(--radius)",
                    padding: "10px 12px", background: row.IsActive ? "#fff" : "#fafafa",
                    opacity: row.IsActive ? 1 : 0.6,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: "var(--text3)",
                        border: "1px solid var(--cs-border)", borderRadius: 4, padding: "1px 5px",
                      }}>
                        #{row.DisplayOrder}
                      </span>
                      <code style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{row.PromptKey}</code>
                      {!row.IsActive && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "#92400e", background: "#fef3c7", padding: "1px 6px", borderRadius: 100 }}>
                          INACTIVE
                        </span>
                      )}
                      {savedFlash === row.PromptKey && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#15803d" }}>✓ Saved</span>
                      )}
                      <div style={{ flex: 1 }} />
                      <button onClick={() => openEdit(row)}
                        style={{ fontSize: 11, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                        Edit
                      </button>
                      <button onClick={() => toggleActive(row)}
                        title={row.IsActive ? "Deactivate — excludes this section from the assembled prompt" : "Reactivate"}
                        style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", padding: "2px 6px", flexShrink: 0 }}>
                        {row.IsActive ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>

                    {row.Notes && (
                      <div style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic", marginBottom: 3 }}>
                        {row.Notes}
                      </div>
                    )}
                    <div style={{ fontSize: 11.5, color: "var(--text2)", lineHeight: 1.5, maxHeight: 46, overflow: "hidden" }}>
                      {preview.slice(0, 280)}{preview.length > 280 ? "…" : ""}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 5 }}>
                      v{row.Version} · updated {fmtWhen(row.UpdatedAt)}{row.UpdatedBy ? ` by ${row.UpdatedBy}` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 30,
  border: "1px solid var(--cs-border)",
  borderRadius: "var(--radius)",
  padding: "0 9px",
  fontSize: 12,
  color: "var(--text)",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
};
